<template>
  <div
    v-if="popupVisible || widgetDisplayType === WIDGET_DISPLAY_TYPES.EMBED"
    class="justify-center"
    :class="{ 'site-selector-popup-background': (!editMode && !demoMode) && widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP }"
    data-test-id="site-selector"
  >
    <q-card
      class="col-auto text-center q-mx-auto"
      :square="square"
      :flat="flat"
      :class="{
        'site-selector-popup': !demoMode && widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP,
        'fixed-center': !demoMode && widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP,
        'absolute-center': demoMode && widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP,
        'q-px-md': !editMode && widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP && !isMobileView && !isImageFullWidth,
        'row justify-center q-px-sm': !isMobileView && !isImageFullWidth,
        'rounded-borders-20': !square,
        'container': widgetDisplayType === WIDGET_DISPLAY_TYPES.EMBED,
        [`bg-${backgroundColor}`]: backgroundColor !== 'custom',
        'bg-responsive': backgroundColor === null,
        'q-pt-none q-pb-md q-px-none': !!isImageFullWidth,
        'q-pt-lg q-pb-xl': !isImageFullWidth
      }"
      style="width: 100%;"
      :style="{
        'transform': demoMode && widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP ? 'translate(-50%, 20%)' : undefined,
        'background-color': backgroundColor === 'custom' ? `${backgroundColorHex}` : undefined,
        'max-width': widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP ? siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.DROPDOWN ? '500px': '800px' : undefined,
        'max-height': $q.screen.xs ? '100%' : undefined,
        'overflow-y': $q.screen.xs ? 'auto' : undefined
      }"
    >
      <q-img
        v-if="showImage && !!imageUrl && !isImageFullWidth"
        :src="imageUrl"
        :ratio="isWidget ? 1 : undefined"
        :class="{ 'absolute': widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP && !isMobileView }"
        style="left:0; right: 0; margin: auto;"
        :style="{
          'transform': widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP && !isMobileView ? 'translateY(-90%)' : undefined,
          width: isWidget ? '100px' : '150px'
        } "
      />
      <q-icon
        v-if="isImageFullWidth"
        name="close"
        color="black"
        size="sm"
        class="q-ma-sm q-pa-sm"
        style="position: absolute; right: 0; z-index: 1; background-color: #dfdfdf79; border-radius: 50%;"
        @click="dismissSiteSelector()"
      />
      <img
        v-if="isImageFullWidth"
        style="width: 100%; position: relative;"
        :src="imageUrl"
      >
      <h2
        v-if="showTitle && !!title"
        class="text-center"
        :class="{
          ['text-' + `${titleColor}`]: titleColor !== 'custom',
          'text-primary': titleColor === null,
          'text-h2': siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.DROPDOWN && isWidget,
          'q-px-xl': !isMobileView,
          'q-mt-xs': isMobileView && imageUrl
        }"
        :style="{ 'color': titleColor === 'custom' ? `${titleColorHex}` : undefined, }"
      >
        {{ title }}
      </h2>
      <p
        v-if="showSubtitle && !showSiteSelectorPopUp"
        class="full-width"
        :class="{
          'text-white': useDarkMode,
          'text-dark': !useDarkMode,
          'q-pt-lg': !showTitle,
          'text-subtitle1': isWidget
        }"
      >
        <span
          :style="{
            'white-space': isWidget ? undefined : 'pre-wrap',
            'margin-right': isWidget ? undefined : '-30px'
          }"
        >
          {{ subtitle }}
        </span>
        <template v-if="!isWidget">
          <q-btn
            v-if="siteDetailLinkUrl"
            flat
            padding="sm none"
            :ripple="false"
            class="button btn-link btn--no-hover text-primary"
            :type="isSiteDirectionsHrefInternal ? 'button' : 'a'"
            :href="isSiteDirectionsHrefInternal ? undefined : siteDirectionsHref"
            :to="isSiteDirectionsHrefInternal ? siteDirectionsHref : undefined"
          >
            {{ $t('siteLocation.directions') }}
          </q-btn>
          <span
            v-if="siteSelectorItems.length > 0"
            style="display: block"
          >{{ $t('siteLocation.selectLocationQuestion') }}
          </span>
        </template>
      </p>

      <div
        v-if="siteSelectorItems.length > 0"
        class="row justify-center items-start full-width q-mt-md equal-height"
      >
        <q-btn-dropdown
          v-if="siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.DROPDOWN"
          label="Please Select"
          :outline="buttonType === BUTTON_TYPES.OUTLINE"
          :flat="buttonType === BUTTON_TYPES.FLAT"
          :unelevated="buttonType === BUTTON_TYPES.UNELEVATED"
          :color="buttonColor !== 'custom' ? buttonColor : undefined"
          :style="{
            'background-color': (buttonType === BUTTON_TYPES.STANDARD || buttonType === BUTTON_TYPES.UNELEVATED) && buttonColor === 'custom' ? buttonColorHex : undefined,
            'border-radius': borderRadius === 0 ? '0' : `${borderRadius}px`,
            'color': (buttonType === BUTTON_TYPES.FLAT || buttonType === BUTTON_TYPES.OUTLINE) && buttonColor === 'custom' ? buttonColorHex : undefined
          }"
          size="xl"
          class="q-px-lg"
          :text-color="(buttonType === BUTTON_TYPES.STANDARD || buttonType === BUTTON_TYPES.UNELEVATED) && buttonColor === 'custom' && buttonColorHex === '#ffffff' ? 'primary' : undefined "
        >
          <q-list
            class="q-px-md"
          >
            <internal-or-external-link
              v-for="(siteSelectorItem, i) in siteSelectorItems"
              :key="i"
              :href="hrefForSiteSelectorItem(siteSelectorItem)"
            >
              <q-item
                v-close-popup
                clickable
                class="text-center"
                :style="{ 'border-top': i === 0 ? undefined : '1px solid var(--q-primary)' }"
              >
                <q-item-section>
                  <q-item-label class="text-h5 text-primary">
                    {{ siteSelectorItem.name }}
                  </q-item-label>
                  <q-item-label
                    v-if="siteSelectorItem.address"
                  >
                    {{ siteSelectorItem.address }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </internal-or-external-link>
          </q-list>
        </q-btn-dropdown>
        <template v-if="siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.BUTTON">
          <q-btn
            v-for="(siteSelectorItem, i ) in siteSelectorItems"
            :key="i"
            size="lg"
            class="q-ma-sm"
            :outline="buttonType === BUTTON_TYPES.OUTLINE"
            :flat="buttonType === BUTTON_TYPES.FLAT"
            :unelevated="buttonType === BUTTON_TYPES.UNELEVATED"
            :color="buttonColor !== 'custom' ? buttonColor : undefined"
            :style="{
              'min-width': isMobileView ? '80%': '180px',
              'background-color': (buttonType === BUTTON_TYPES.STANDARD || buttonType === BUTTON_TYPES.UNELEVATED) && buttonColor === 'custom' ? buttonColorHex : undefined,
              'border-radius': borderRadius === 0 ? '0' : `${borderRadius}px`,
              'color': (buttonType === BUTTON_TYPES.FLAT || buttonType === BUTTON_TYPES.OUTLINE) && buttonColor === 'custom' ? buttonColorHex : undefined
            }"
            :text-color="(buttonType === BUTTON_TYPES.STANDARD || buttonType === BUTTON_TYPES.UNELEVATED) && buttonColor === 'custom' && buttonColorHex === '#ffffff' ? 'primary' : undefined "
            :to="$isUrlSameHostName(hrefForSiteSelectorItem(siteSelectorItem, false)) ? hrefForSiteSelectorItem(siteSelectorItem, true) : undefined"
            :href="$isUrlSameHostName(hrefForSiteSelectorItem(siteSelectorItem, false)) ? undefined : hrefForSiteSelectorItem(siteSelectorItem, false)"
          >
            <div
              class="text-center text-weight-bold"
              :class="{ 'q-pt-xs': siteSelectorItem.address }"
              style="line-height: 1;"
            >
              {{ siteSelectorItem.name }}<br>
              <small
                v-if="siteSelectorItem.address "
                class="ellipsis text-caption"
                style="display: block; margin-top: -3px;"
              >{{ siteSelectorItem.address }}
              </small>
            </div>
          </q-btn>
        </template>
        <template v-if="siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.BLOCK">
          <internal-or-external-link
            v-for="(siteSelectorItem, i) in siteSelectorItems"
            :key="i"
            class="block q-ma-sm"
            :href="hrefForSiteSelectorItem(siteSelectorItem)"
            :style="{ width: isMobileView ? '50%' : '30%' }"
          >
            <q-card
              :class="{
                'text-center': blockTextCentred,
                'text-left': !blockTextCentred,
                'no-shadow': blockShadow === 0,
                ['bg-' + `${blockBackgroundColor}`]: blockBackgroundColor !== 'custom',
                ['shadow-' + `${blockShadow}`]: blockShadow !== 0
              }"
              :style="{
                'background-color': blockBackgroundColor === 'custom' ? blockBackgroundColorHex : undefined,
                'border-radius': borderRadius === 0 ? '0' : `${borderRadius}px`,
              }"
            >
              <img
                v-if="siteSelectorItem.image"
                :src="siteSelectorItemImageUrl(siteSelectorItem.image)"
              >
              <q-card-section
                style="min-height: 70px;"
              >
                <div
                  class="text-h6"
                  :class="blockTextColor !== 'custom' ? 'text-' + blockTextColor : undefined"
                  :style="{ 'color': blockTextColor === 'custom' ? blockTextColorHex : undefined }"
                >
                  {{ siteSelectorItem.name }}
                </div>
                <div
                  v-if="siteSelectorItem.address"
                  class="text-subtitle2"
                  :class="blockTextColor !== 'custom' ? 'text-' + blockTextColor : undefined"
                  :style="{ 'color': blockTextColor === 'custom' ? blockTextColorHex : undefined }"
                >
                  {{ siteSelectorItem.address }}
                </div>
              </q-card-section>
            </q-card>
          </internal-or-external-link>
        </template>
      </div>
      <site-selector-pop-up
        v-if="showSiteSelectorPopUp && widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP && !isMoviePage"
        :use-dark-mode="useDarkMode"
        :show-change-location-button="showChangeLocationButton"
        @dismiss-site-selector="dismissSiteSelector()"
        @show-site-selector-drawer="$emit('show-site-selector-drawer')"
      />

      <q-btn
        v-if="allowDismiss && (widgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP || editMode) && !isImageFullWidth"
        class="q-mt-lg"
        flat
        :label="$t('actions.cancel')"
        :class="useDarkMode ? 'text-white' : 'text-dark'"
        @click="dismissSiteSelector()"
      />
    </q-card>
  </div>
</template>

<script>
import { BUTTON_TYPES, IMAGE_POSITION_TYPES, POPUP_TRIGGER_TYPES, SITE_SELECTOR_DISPLAY_TYPES, TEXT_FIELD_TYPES, WIDGET_DISPLAY_TYPES } from 'src/models/page/Section'
import { siteSelectorItemImageUrl } from 'src/models/page/SiteSelectorItem'
import InternalOrExternalLink from 'src/modules/consumer/components/InternalOrExternalLink'
import SiteSelectorPopUp from 'src/modules/consumer/components/site-selector/SiteSelectorPopUp'

export default {
  name: 'SiteSelector',
  components: {
    InternalOrExternalLink,
    SiteSelectorPopUp
  },
  configuration: [
    'currentSite', 'sites'
  ],
  context: ['currentUser'],
  props: {
    showSiteSelectorPopUp: {
      type: Boolean,
      required: false,
      default: false
    },
    backgroundColor: {
      type: String,
      required: false,
      default: null
    },
    backgroundColorHex: {
      type: String,
      required: false,
      default: null
    },
    widthMode: {
      type: String,
      required: false,
      default: 'Centered'
    },
    square: {
      type: Boolean,
      required: false,
      default: true
    },
    flat: {
      type: Boolean,
      required: false,
      default: true
    },
    popupTriggerType: {
      type: String,
      required: false,
      default: POPUP_TRIGGER_TYPES.EXIT
    },
    delayInSeconds: {
      type: Number,
      required: false,
      default: 6
    },
    title: {
      type: String,
      required: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    titleColor: {
      type: String,
      required: false,
      default: 'default'
    },
    titleColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    showSubtitle: {
      type: Boolean,
      default: false
    },
    subtitle: {
      type: String,
      default: ''
    },
    buttonLabel: {
      type: String,
      default: 'Subscribe'
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.OUTLINE
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'dark'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 0
    },
    blockBackgroundColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    blockBackgroundColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    blockTextColor: {
      type: String,
      required: false,
      default: 'custom'
    },
    blockTextColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    blockTextCentred: {
      type: Boolean,
      default: false
    },
    blockShadow: {
      type: Number,
      required: false,
      default: 0
    },
    textFieldType: {
      type: String,
      required: false,
      default: 'filled'
    },
    outlineTextField: {
      type: Boolean,
      default: false
    },
    demoMode: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: Boolean,
      default: false
    },
    useDarkMode: {
      type: Boolean,
      default: false
    },
    showImage: {
      type: Boolean,
      default: false
    },
    imageUrl: {
      type: String,
      default: undefined
    },
    widgetDisplayType: {
      type: String,
      required: true,
      default: WIDGET_DISPLAY_TYPES.POPUP
    },
    siteSelectorItems: {
      type: Array,
      required: true
    },
    siteSelectorsDisplayType: {
      type: String,
      required: true,
      default: SITE_SELECTOR_DISPLAY_TYPES.DROPDOWN
    },
    allowDismiss: {
      type: Boolean,
      default: false
    },
    isWidget: {
      type: Boolean,
      default: true
    },
    siteDetailLinkUrl: {
      type: String,
      default: null
    },
    isPhonePreview: {
      type: Boolean,
      default: false,
      required: false
    },
    showChangeLocationButton: {
      type: Boolean,
      default: false,
      required: false
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    isImageFullWidth: {
      type: Boolean,
      default: false,
      required: false
    },
    isMoviePage: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  emits: ['show-site-selector-drawer', 'dismiss-site-selector'],
  data () {
    return {
      popupVisible: false
    }
  },
  computed: {
    siteDirectionsHref () {
      if (!this.isWidget && this.siteDetailLinkUrl) {
        return this.$appendCurrentQueryParams(this.siteDetailLinkUrl)
      }
      return undefined
    },
    isSiteDirectionsHrefInternal () {
      return this.siteDirectionsHref && _.startsWith(this.siteDirectionsHref, '/')
    }
  },
  watch: {
    delayInSeconds () {
      this.popupVisible = false
      this.initPopup()
    },
    demoMode () {
      this.popupVisible = true
    }
  },
  mounted () {
    this.initPopup()
  },
  created () {
    _.extend(this, { siteSelectorItemImageUrl, BUTTON_TYPES, TEXT_FIELD_TYPES, IMAGE_POSITION_TYPES, WIDGET_DISPLAY_TYPES, SITE_SELECTOR_DISPLAY_TYPES })
  },
  methods: {
    hrefForSiteSelectorItem (siteSelectorItem, pathOnly = false) {
      if (!_.isEmpty(siteSelectorItem.externalUrl)) {
        return this.$appendCurrentQueryParams(siteSelectorItem.externalUrl)
      }

      const selectedSite = _.find(this.sites.data, site => site.id === siteSelectorItem.siteId)
      if (selectedSite) {
        const siteUrl = new URL(this.$appendCurrentQueryParams(`${selectedSite.url(window.location.pathname)}`))
        return pathOnly ? `${siteUrl.pathname}${siteUrl.search}${siteUrl.hash}` : siteUrl.href
      } else {
        return '#'
      }
    },

    triggerPopup () {
      if (this.delayInSeconds === 0 || !this.isWidget || this.demoMode || this.editMode) {
        this.popupVisible = true
      } else {
        setTimeout(() => { this.popupVisible = true }, this.delayInSeconds * 1000)
      }
    },

    initPopup () {
      if (this.widgetDisplayType === WIDGET_DISPLAY_TYPES.EMBED) return

      this.triggerPopup()
    },
    dismissSiteSelector () {
      if (this.isWidget) {
        this.popupVisible = false
      } else {
        this.$emit('dismiss-site-selector', false)
      }
    }
  }
}
</script>
<style>
.site-selector-popup-background:after{
  content: "";
  height: 120vh;
  width: 100vw;
  display: block;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  position: fixed;
  top: -20vh;
  left: 0;
}
.site-selector-popup{
  z-index: 2001!important;
}

</style>
